import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BriefcaseBusinessIcon,
  CalendarClockIcon,
  CalendarIcon,
  CalendarPlusIcon,
  ChartColumnIcon,
  ClipboardListIcon,
  FileSignatureIcon,
  FilesIcon,
  HomeIcon,
  ListChecksIcon,
  ListTodoIcon,
  MessageSquarePlusIcon,
  MessageSquareIcon,
  NotebookPenIcon,
  SendIcon,
  UploadIcon,
  UserPlusIcon,
  Users2Icon,
  type LucideIcon,
} from 'lucide-react';
import { useDialog } from '@gokrew/ui/context';
import { OnboardingFeatureKey } from '@gokrew/models/company';
import type { InviteCredentials } from '@gokrew/ui/components/InviteSuccessDialog';
import { TranslationNS } from '@/constants/translation-namespaces';
import { usePermissions } from '@/context/PermissionContext';
import { useModuleEnabled } from '@/hooks/useModuleEnabled';
import { useAddEmployeeDialog } from '@/hooks/employees/useAddEmployeeDialog';
import { CompanyRequestLeaveForm } from '@/pages/leave-management/overview/RequestLeaveForm';
import leaveStyles from '@/pages/leave-management/LeaveManagement.module.css';
import { permissionKeys } from '@/utility';
import type { PermissionScope } from '@/types/permissions';

export type CommandGroupId = 'actions' | 'navigate';

export interface PaletteCommand {
  id: string;
  group: CommandGroupId;
  label: string;
  icon: LucideIcon;
  /** Extra words the search also matches (synonyms, French/English twins). */
  keywords: string[];
  /** The second key of a "G then …" jump, for navigation commands. */
  chord?: string;
  /** Press it inside the open palette (empty search) to run a quick action. */
  key?: string;
  run: () => void;
}

interface Options {
  onInviteSuccess: (credentials: InviteCredentials) => void;
}

interface Definition {
  id: string;
  group: CommandGroupId;
  labelKey: string;
  icon: LucideIcon;
  keywords?: string[];
  chord?: string;
  key?: string;
  scope?: PermissionScope | PermissionScope[];
  hidden?: boolean;
  run: () => void;
  /** Labels that live in the sidebar namespace instead of `common`. */
  ns?: 'sidebar';
}

/**
 * What the ⌘K palette offers, already filtered to what this admin can do: a
 * command whose scope the viewer lacks, or whose module the company switched
 * off, is not in the list at all (same rule as the sidebar).
 */
export const useCommandPaletteCommands = ({
  onInviteSuccess,
}: Options): PaletteCommand[] => {
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { permissions } = usePermissions();
  const isModuleOn = useModuleEnabled();
  const openAddEmployee = useAddEmployeeDialog();
  const { t } = useTranslation(TranslationNS.Common);
  const { t: tSidebar } = useTranslation(TranslationNS.sidebar);
  const { t: tLeave } = useTranslation(TranslationNS.LeaveManagement);
  const { t: tEmployee } = useTranslation(TranslationNS.Employee);

  const go = useCallback((path: string) => () => navigate(path), [navigate]);

  const addEmployee = useCallback(
    () =>
      openAddEmployee({
        title: tEmployee('employee.dialog_titles.add_employee'),
        onInviteSuccess,
      }),
    [openAddEmployee, tEmployee, onInviteSuccess]
  );

  const requestLeave = useCallback(
    () =>
      openDialog({
        title: tLeave('dialog_title.request_leave'),
        content: <CompanyRequestLeaveForm onNavigate={navigate} />,
        containerClass: leaveStyles.dialog,
      }),
    [openDialog, tLeave, navigate]
  );

  return useMemo(() => {
    const definitions: Definition[] = [
      // Things an HR admin does all day.
      {
        id: 'add-employee',
        group: 'actions',
        key: 'e',
        labelKey: 'command_palette.actions.add_employee',
        icon: UserPlusIcon,
        keywords: ['new employee', 'hire', 'create employee', 'employé'],
        scope: permissionKeys.employee.create,
        run: addEmployee,
      },
      {
        id: 'new-message',
        group: 'actions',
        key: 'm',
        labelKey: 'command_palette.actions.new_message',
        icon: MessageSquarePlusIcon,
        keywords: ['compose', 'message', 'chat', 'écrire', 'nouveau message'],
        run: go('/messages?compose=1'),
      },
      {
        id: 'request-leave',
        group: 'actions',
        key: 'l',
        labelKey: 'command_palette.actions.request_leave',
        icon: CalendarPlusIcon,
        keywords: ['time off', 'vacation', 'absence', 'congé', 'demander'],
        scope: permissionKeys.leave_request.create,
        run: requestLeave,
      },
      {
        id: 'create-task',
        group: 'actions',
        key: 't',
        labelKey: 'command_palette.actions.create_task',
        icon: ListTodoIcon,
        keywords: ['new task', 'todo', 'tâche'],
        scope: permissionKeys.task_management.create,
        run: go('/tasks/create'),
      },
      {
        id: 'review-leave',
        group: 'actions',
        key: 'a',
        labelKey: 'command_palette.actions.review_leave',
        icon: CalendarIcon,
        keywords: ['approve', 'pending', 'requests', 'approuver', 'demandes'],
        scope: [
          permissionKeys.leave_management.read,
          permissionKeys.leave_request.read,
        ],
        run: go('/leave-management/overview'),
      },
      {
        id: 'new-esignature',
        group: 'actions',
        key: 's',
        labelKey: 'command_palette.actions.new_esignature',
        icon: SendIcon,
        keywords: ['sign', 'contract', 'document', 'signature'],
        scope: permissionKeys.esignature.create,
        run: go('/esignatures/requests/new'),
      },
      {
        id: 'create-survey',
        group: 'actions',
        key: 'u',
        labelKey: 'command_palette.actions.create_survey',
        icon: ClipboardListIcon,
        keywords: ['new survey', 'poll', 'sondage'],
        scope: permissionKeys.survey.create,
        run: go('/survey/create'),
      },
      {
        id: 'import-employees',
        group: 'actions',
        key: 'i',
        labelKey: 'command_palette.actions.import_employees',
        icon: UploadIcon,
        keywords: ['csv', 'bulk', 'upload', 'importer'],
        scope: permissionKeys.employee.create,
        run: go('/employees/bulk-imports'),
      },

      // Jump to a page: "G" then the letter.
      {
        id: 'go-dashboard',
        group: 'navigate',
        labelKey: 'nav.dashboard',
        ns: 'sidebar',
        icon: HomeIcon,
        chord: 'd',
        scope: permissionKeys.dashboard.read,
        run: go('/dashboard'),
      },
      {
        id: 'go-employees',
        group: 'navigate',
        labelKey: 'nav.employees',
        ns: 'sidebar',
        icon: Users2Icon,
        keywords: ['people', 'staff', 'employés'],
        chord: 'e',
        scope: permissionKeys.employee.read,
        run: go('/employees'),
      },
      {
        id: 'go-leave',
        group: 'navigate',
        labelKey: 'nav.leave_management',
        ns: 'sidebar',
        icon: CalendarIcon,
        keywords: ['time off', 'vacation', 'congés'],
        chord: 'l',
        scope: [
          permissionKeys.leave_management.read,
          permissionKeys.leave_request.read,
        ],
        run: go('/leave-management'),
      },
      {
        id: 'go-tasks',
        group: 'navigate',
        labelKey: 'nav.tasks',
        ns: 'sidebar',
        icon: ListChecksIcon,
        keywords: ['task', 'workflow', 'onboarding', 'tâches'],
        scope: permissionKeys.task_management.read,
        run: go('/tasks'),
      },
      {
        id: 'go-messages',
        group: 'navigate',
        labelKey: 'nav.messages',
        ns: 'sidebar',
        icon: MessageSquareIcon,
        keywords: ['inbox', 'chat', 'messages', 'messagerie'],
        chord: 'm',
        run: go('/messages'),
      },
      {
        id: 'go-my-tasks',
        group: 'navigate',
        labelKey: 'nav.my_tasks',
        ns: 'sidebar',
        icon: ListTodoIcon,
        chord: 't',
        run: go('/my-tasks'),
      },
      {
        id: 'go-recruiting',
        group: 'navigate',
        labelKey: 'nav.recruiting',
        ns: 'sidebar',
        icon: BriefcaseBusinessIcon,
        keywords: ['jobs', 'candidates', 'applicants', 'ats', 'recrutement'],
        chord: 'r',
        hidden: !isModuleOn(OnboardingFeatureKey.Recruiting),
        scope: permissionKeys.recruiting.read,
        run: go('/ats'),
      },
      {
        id: 'go-timesheet',
        group: 'navigate',
        labelKey: 'nav.timesheet',
        ns: 'sidebar',
        icon: CalendarClockIcon,
        keywords: ['hours', 'feuilles de temps'],
        chord: 's',
        hidden: !isModuleOn(OnboardingFeatureKey.Timesheet),
        scope: permissionKeys.timesheet.read,
        run: go('/timesheet'),
      },
      {
        id: 'go-analytics',
        group: 'navigate',
        labelKey: 'nav.analytics',
        ns: 'sidebar',
        icon: ChartColumnIcon,
        keywords: ['reports', 'rapports'],
        chord: 'a',
        scope: permissionKeys.analytics.read,
        run: go('/analytics'),
      },
      {
        id: 'go-esignatures',
        group: 'navigate',
        labelKey: 'nav.esignatures',
        ns: 'sidebar',
        icon: FileSignatureIcon,
        keywords: ['sign', 'signature'],
        chord: 'g',
        scope: permissionKeys.esignature.read,
        run: go('/esignatures'),
      },
      {
        id: 'go-surveys',
        group: 'navigate',
        labelKey: 'sidebar.surveys',
        ns: 'sidebar',
        icon: ClipboardListIcon,
        keywords: ['survey', 'poll', 'sondages'],
        chord: 'u',
        scope: permissionKeys.survey.read,
        run: go('/survey'),
      },
      {
        id: 'go-files',
        group: 'navigate',
        labelKey: 'nav.files',
        ns: 'sidebar',
        icon: FilesIcon,
        keywords: ['documents', 'resources', 'fichiers'],
        chord: 'f',
        hidden: !isModuleOn(OnboardingFeatureKey.Files),
        scope: permissionKeys.file_management.read,
        run: go('/files'),
      },
      {
        id: 'go-notes',
        group: 'navigate',
        labelKey: 'nav.notes',
        ns: 'sidebar',
        icon: NotebookPenIcon,
        keywords: ['announcements', 'annonces'],
        chord: 'n',
        hidden: !isModuleOn(OnboardingFeatureKey.Notes),
        scope: [
          permissionKeys.hr_notes.read,
          permissionKeys.employee_notes.read,
          permissionKeys.announcement.read,
        ],
        run: go('/notes'),
      },
    ];

    return definitions
      .filter((d) => {
        if (d.hidden) return false;
        const scopes = d.scope === undefined ? [] : [d.scope].flat();
        return scopes.length === 0 || scopes.some((s) => permissions.has(s));
      })
      .map((d) => ({
        id: d.id,
        group: d.group,
        icon: d.icon,
        chord: d.chord,
        key: d.key,
        run: d.run,
        keywords: d.keywords ?? [],
        label: d.ns === 'sidebar' ? tSidebar(d.labelKey) : t(d.labelKey),
      }));
  }, [addEmployee, requestLeave, go, isModuleOn, permissions, t, tSidebar]);
};
